import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Spin, Form, Input,
  Select, Row, Col, Space, Button, DatePicker,
  Alert, Checkbox, Upload, Avatar, message, Divider
} from 'antd';
import { UserOutlined, TeamOutlined, IdcardOutlined, PhoneOutlined, MailOutlined, CalendarOutlined, AppstoreOutlined, TagOutlined, CameraOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Gender } from '../../actions/constants/constant';
import { userActions } from '../../actions/userActions';
import dayjs from 'dayjs';
import { getTenantId } from '../../services/helper';

const { Option } = Select;

interface UsersFormModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: any, file: any) => void;
  loading?: boolean;
  roleData?: any[];
  currentUserCounts?: { students: number; instructors: number };
}

const UsersFormModal: React.FC<UsersFormModalProps> = ({
  open,
  onCancel,
  onSubmit,
  loading,
  roleData,
  currentUserCounts
}) => {

  const [form] = Form.useForm();
  const [subscriptionLimits, setSubscriptionLimits] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [isManualOnboarding, setIsManualOnboarding] = useState<boolean>(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  const service = useSelector((state: any) => state.getServiceReducer.servicedata);
  const categories = useSelector((state: any) => state.getCategoryReducer.categorydata);
  const userViewState = useSelector((state: any) => state.userViewReducer);
  const getTermsByTypeState = useSelector((state: any) => state.getTermsByTypeReducer);
  const auth = JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
  const dispatch = useDispatch();

  useEffect(() => {
    try {
      const catId = form.getFieldValue('category');
      setSelectedCategoryId(catId || '');
    } catch (e) { }
  }, [form]);

  useEffect(() => {
    if (getTermsByTypeState) {
      console.log("getTermsandAccepted:", getTermsByTypeState);

    }
  }, [getTermsByTypeState]);

  const categoryOptions = useMemo(() => {
    const payload = categories?.categories;
    const list = payload?.categories ?? payload ?? [];
    const filtered = (Array.isArray(list) ? list : []).filter((c: any) => c.categoryType !== 'QUERY');
    return filtered;
  }, [categories]);

  useEffect(() => {
    if (open && auth?._id) {
      dispatch(userActions.userViewRequest({ id: auth._id }));
    }
    if (open) {
      form.resetFields();
      setSelectedRole('');
      setIsManualOnboarding(false);
      setProfileImageUrl('');
      setProfileImageFile(null);
    }
  }, [open, auth?._id, dispatch, form]);

  useEffect(() => {
    if (userViewState?.data?.currentSubscription?.plan_id) {
      const plan = userViewState.data.currentSubscription.plan_id;
      setSubscriptionLimits({
        maxStudents: plan.maxStudents,
        maxInstructors: plan.maxInstructors,
        planName: plan.name,
      });
    }
  }, [userViewState]);

  const handleFinish = (values: any) => {
    const submissionData: any = { ...values, isManualOnboarding , tenantId: getTenantId() };
    if (getTermsByTypeState?.termsData?._id) {
      submissionData.termsAndConditions = getTermsByTypeState.termsData._id;
    }
    onSubmit(submissionData, profileImageFile);
  };

  const handleRoleChange = (roleId: string) => {
    const selectedRoleObj = roleData?.find((r: any) => r._id === roleId);
    const roleName = (selectedRoleObj?.role ?? selectedRoleObj?.roleName ?? '').toString().toUpperCase();
    setSelectedRole(roleName);
    if (roleName === 'STUDENT') {
      dispatch(userActions.getTermsByTypeRequest('admin_consent'));
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error('Image must be smaller than 3MB!');
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = () => setProfileImageUrl(reader.result as string);
    reader.readAsDataURL(file);
    setProfileImageFile(file);
    return false;
  };

  const handleRemoveImage = () => {
    setProfileImageUrl('');
    setProfileImageFile(null);
  };

  const isLimitReached = () => {
    if (!subscriptionLimits || !selectedRole) return false;
    if (selectedRole === 'STUDENT' && subscriptionLimits.maxStudents !== null) {
      return (currentUserCounts?.students || 0) >= subscriptionLimits.maxStudents;
    }
    if (selectedRole === 'INSTRUCTOR' && subscriptionLimits.maxInstructors !== null) {
      return (currentUserCounts?.instructors || 0) >= subscriptionLimits.maxInstructors;
    }
    return false;
  };

  const getLimitMessage = () => {
    if (!subscriptionLimits || !selectedRole) return null;
    if (selectedRole === 'STUDENT' && subscriptionLimits.maxStudents !== null) {
      const current = currentUserCounts?.students || 0;
      const max = subscriptionLimits.maxStudents;
      if (current >= max) {
        return {
          type: 'error' as const,
          message: `Student limit reached (${current}/${max})`,
          description: `Your ${subscriptionLimits.planName} plan allows maximum ${max} students. Please upgrade to add more.`,
        };
      } else if (current >= max * 0.8) {
        return {
          type: 'warning' as const,
          message: `Approaching student limit (${current}/${max})`,
          description: `You're approaching your plan limit. Consider upgrading soon.`,
        };
      }
    }
    if (selectedRole === 'INSTRUCTOR' && subscriptionLimits.maxInstructors !== null) {
      const current = currentUserCounts?.instructors || 0;
      const max = subscriptionLimits.maxInstructors;
      if (current >= max) {
        return {
          type: 'error' as const,
          message: `Instructor limit reached (${current}/${max})`,
          description: `Your ${subscriptionLimits.planName} plan allows maximum ${max} instructors. Please upgrade to add more.`,
        };
      } else if (current >= max * 0.8) {
        return {
          type: 'warning' as const,
          message: `Approaching instructor limit (${current}/${max})`,
          description: `You're approaching your plan limit. Consider upgrading soon.`,
        };
      }
    }
    return null;
  };

  const limitMessage = getLimitMessage();
  const limitReached = isLimitReached();

  return (
    <Drawer
      className="ufd-drawer"
      open={open}
      onClose={onCancel}
      size={window.innerWidth > 1400 ? 530 : 450}
      closable={false}
      destroyOnClose
      title={null}
      footer={
        <div className="ufd-drawer-footer">
          <Button className="ufd-btn-cancel" onClick={onCancel} disabled={!!loading}>
            Cancel
          </Button>
          <Button
            className="ufd-btn-save"
            type="primary"
            onClick={() => form.submit()}
            loading={!!loading}
            disabled={!!loading || limitReached}
          >
            Save User
          </Button>
        </div>
      }
    >
      {/* ── Sticky Header ── */}
      <div className="ufd-drawer-header">
        <div className="ufd-drawer-header-inner">
          <div className="ufd-drawer-title-group">
            <div className="ufd-drawer-icon-box">
              <UserOutlined />
            </div>
            <div className="ufd-drawer-title-text">
              <span className="ufd-drawer-title">Add New User</span>
              <span className="ufd-drawer-subtitle">Fill in the details to create a new user account</span>
            </div>
          </div>
          <button className="ufd-drawer-close-btn" onClick={onCancel} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
        <Divider className="ufd-drawer-header-divider" />
      </div>

      <Spin spinning={!!loading} tip="Creating user...">
        <Form form={form} layout="vertical" onFinish={handleFinish} className="ufd-form">

          {/* ── Subscription limit alert ── */}
          {limitMessage && (
            <div className="ufd-alert-wrap">
              <Alert
                message={limitMessage.message}
                description={limitMessage.description}
                type={limitMessage.type}
                showIcon
                closable={limitMessage.type === 'warning'}
                className="ufd-alert"
              />
            </div>
          )}

          {/* ── Section: Role ── */}
          <div className="ufd-form-section">


            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select a role' }]}
              className="ufd-form-item"
            >
              <Select
                className="ufd-select"
                placeholder="Select a role for this user"
                onChange={handleRoleChange}
              >
                {roleData && Array.isArray(roleData) &&
                  roleData.map((r: any) => (
                    <Option key={r._id ?? r.role} value={r._id}>
                      {r.roleName ?? r.role}
                    </Option>
                  ))
                }
              </Select>
            </Form.Item>
          </div>

          {/* ── Profile Picture (Students only) ── */}
          {selectedRole === 'STUDENT' && (
            <>
              <div className="ufd-form-section">
                <div className="ufd-section-header">
                  <CameraOutlined className="ufd-section-icon" />
                  <span className="ufd-section-title">Profile Picture</span>
                </div>

                <div className="ufd-avatar-block">
                  <div className="ufd-avatar-preview">
                    <Avatar
                      size={80}
                      src={profileImageUrl || undefined}
                      icon={!profileImageUrl ? <UserOutlined /> : undefined}
                      className="ufd-avatar"
                    />
                    <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                      <button className="ufd-avatar-upload-btn" type="button" aria-label="Upload photo">
                        <CameraOutlined />
                      </button>
                    </Upload>
                  </div>
                  <div className="ufd-avatar-meta">
                    <span className="ufd-avatar-label">
                      Profile Photo
                    </span>
                    <span className="ufd-avatar-hint">JPG, GIF or PNG. Max size 2MB</span>
                    <div className="ufd-avatar-actions">
                      <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                        <Button type="link" size="small" className="ufd-upload-link">
                          Upload photo
                        </Button>
                      </Upload>
                      {profileImageUrl && (
                        <>
                          <span className="ufd-avatar-sep">|</span>
                          <Button
                            type="link"
                            size="small"
                            danger
                            className="ufd-remove-link"
                            onClick={handleRemoveImage}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}

          {/* ── Section: Personal Information ── */}
          <div className="ufd-form-section">


            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                  className="ufd-form-item"
                >
                  <Input className="ufd-input" placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                  className="ufd-form-item"
                >
                  <Input className="ufd-input" placeholder="Enter last name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="gender"
                  label="Gender"
                  rules={[{ required: true, message: 'Please select gender' }]}
                  className="ufd-form-item"
                >
                  <Select className="ufd-select" placeholder="Select gender">
                    {Gender.map((g) => (
                      <Option key={g} value={g}>{g}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="dateOfBirth"
                  label="Date of Birth"
                  rules={[
                    { required: true, message: 'Please select DOB' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const maxDate = dayjs().subtract(18, 'year');
                        if (dayjs(value).isAfter(maxDate)) {
                          return Promise.reject('Must be at least 18 years old');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  className="ufd-form-item"
                >
                  <DatePicker
                    className="ufd-datepicker"
                    placeholder="Select DOB"
                    suffixIcon={<CalendarOutlined className="ufd-datepicker-icon" />}
                    disabledDate={(current) =>
                      current && current.isAfter(dayjs().subtract(18, 'year'))
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider className="ufd-section-divider" />
          </div>



          {/* ── Section: Contact Details ── */}
          <div className="ufd-form-section">

            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter email' },
                {
                  pattern: /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/,
                  message: 'Please enter a valid email address',
                },
              ]}
              className="ufd-form-item"
            >
              <Input
                className="ufd-input"
                placeholder="email@example.com"
                prefix={<MailOutlined className="ufd-input-prefix-icon" />}
              />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label="Phone"
              rules={[{ required: true, message: 'Please enter phone number' }]}
              className="ufd-form-item"
            >
              <Input
              maxLength={10}
                onKeyPress={(e) => {
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                className="ufd-input"
                placeholder="Enter phone number"
                prefix={<PhoneOutlined className="ufd-input-prefix-icon" />}
              />
            </Form.Item>
          </div>

          {/* ── Section: Enrollment (Students only) ── */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) => {
              const selectedRoleId = getFieldValue('role') ?? roleData?.find(
                (r: any) => ((r.role ?? r.roleName) ?? '').toString().toLowerCase() === 'student'
              )?._id;
              const selectedRoleObj = roleData?.find((r: any) => r._id === selectedRoleId);
              const role = (selectedRoleObj?.role ?? selectedRoleObj?.roleName ?? '').toString().toUpperCase();

              if (role !== 'STUDENT') return null;

              return (
                <>
                  <div className="ufd-form-section">


                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="category" label="Category" className="ufd-form-item">
                          <Select
                            className="ufd-select"
                            placeholder="Select category"
                            onChange={(value) => {
                              form.setFieldsValue({ service: undefined });
                              setSelectedCategoryId(value);
                            }}
                          >
                            {categoryOptions.map((r: any) => (
                              <Option key={r._id} value={r._id}>{r.categoryName}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, curValues) => prevValues.category !== curValues.category}
                        >
                          {() => {
                            const category = form.getFieldValue('category') || selectedCategoryId;
                            const allServices = (service && service.services) || [];
                            const filteredServices = category
                              ? allServices.filter((s: any) => {
                                const serviceCategoryId = s?.category?._id || s?.category;
                                return String(serviceCategoryId) === String(category);
                              })
                              : [];
                            return (
                              <Form.Item name="service" label="Service" className="ufd-form-item">
                                <Select
                                  className="ufd-select"
                                  placeholder={category ? 'Select service' : 'Select category first'}
                                  disabled={!category}
                                >
                                  {filteredServices.map((serviceItem: any) => (
                                    <Option key={serviceItem._id} value={serviceItem._id}>
                                      {serviceItem.serviceName}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </>
              );
            }}
          </Form.Item>

          {/* ── Section: Consent (Students only) ── */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) => {
              const selectedRoleId = getFieldValue('role');
              const selectedRoleObj = roleData?.find((r: any) => r._id === selectedRoleId);
              const role = (selectedRoleObj?.role ?? selectedRoleObj?.roleName ?? '').toString().toUpperCase();

              if (role !== 'STUDENT') return null;

              return (
                <>
                  <Divider className="ufd-section-divider" />
                  <div className="ufd-form-section ufd-form-section--last">
                    <div className="ufd-section-header">
                      <TagOutlined className="ufd-section-icon" />
                      <span className="ufd-section-title">Consent - Check this if you are adding a user's past record </span>
                    </div>

                    <div className={`ufd-consent-panel${isManualOnboarding ? ' ufd-consent-panel--checked' : ''}`}>
                      <Checkbox
                        checked={isManualOnboarding}
                        onChange={(e) => setIsManualOnboarding(e.target.checked)}
                        className="ufd-consent-checkbox"
                        aria-label="Confirm consent to add user's data"
                      />
                      <div className="ufd-consent-content">
                        <span className="ufd-consent-title">
                          {getTermsByTypeState?.termsData?.content?.[0]?.header
                            ?? "I confirm that I have obtained consent to add this user's data"}
                        </span>
                        <span className={`ufd-consent-desc${isManualOnboarding ? ' ufd-consent-desc--checked' : ''}`}>
                          
                            {getTermsByTypeState?.termsData?.content?.[0]?.content?.[0]}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              );
            }}
          </Form.Item>

        </Form>
      </Spin>
    </Drawer>
  );
};

export default UsersFormModal;
